import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, message, Modal, ModalProps, Select, Spin, Typography } from 'antd';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import useAPIQuery from '../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../api/useInfiniteAPIQuery';
import ErrorResult from '../../components/ErrorResult';
import Form from '../../components/Form';
import PageContent from '../../components/PageContent';
import useAllowedUsersMutation from '../../hooks/useAllowedUsersMutation';
import useDiscardChangesModal from '../../hooks/useDiscardChangesModal';
import usePermissionCheckerQuery from '../../hooks/usePermissionCheckerQuery';
import usePrefetchPages from '../../hooks/usePrefetchPages';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import { JobPostingId } from '../../types';

interface AllowedUsersFormValues {
  recruiter?: number;
  manager?: number;
  related?: number[];
  observers?: number[];
}

interface ConfirmSelfRemoveModalProps extends ModalProps {
  onSuccess?: () => void | Promise<unknown>;
}

export const ConfirmSelfRemoveModal = ({ ...props }: ConfirmSelfRemoveModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal
      okButtonProps={{
        htmlType: 'submit',
      }}
      okText={t('Sim, remover')}
      title={
        <Typography.Text>
          <InfoCircleOutlined style={{ color: 'red', marginRight: '2%' }} />
          <Trans>Remover-se da vaga?</Trans>
        </Typography.Text>
      }
      {...props}
    >
      <Trans>
        Ao remover-se você não terá mais acesso à essa vaga. Tem certeza que deseja continuar?
      </Trans>
    </Modal>
  );
};

const AllowedUsersForm = () => {
  const { t } = useTranslation();

  const { tenant, id } = useTypedParams({ tenant: String, id: JobPostingId });
  const navigate = useTypedNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const recruiterValue = Form.useWatch('recruiter', form);
  const managerValue = Form.useWatch('manager', form);
  const relatedValue = Form.useWatch('related', form);
  const [leavePage, setLeavePage] = useState<boolean>(false);
  const [confirmSelfRemoveModalIsOpen, setConfirmSelfRemoveModalIsOpen] = useState<boolean>(false);
  const [selfRemove, setSelfRemove] = useState(false);
  const [setIsDirty] = useDiscardChangesModal(form);

  const queryClient = useQueryClient();

  const viewAllVacanciesPermission = usePermissionCheckerQuery(['view_all_vacancies']);

  const jobPosting = useAPIQuery(`/job-postings/${id}/`);
  const usersPages = useInfiniteAPIQuery([
    '/users/',
    {
      params: {
        is_active: true,
      },
    },
  ]);
  const users = usePrefetchPages(usersPages, (data) => data.results);
  const user = useAPIQuery('/users/me/');

  const recruitersPages = useInfiniteAPIQuery([
    '/recruiters/',
    {
      params: {
        job_posting_id: id,
      },
    },
  ]);

  const recruiters = usePrefetchPages(recruitersPages, (data) => data.results);

  const mutation = useAllowedUsersMutation({
    onSuccess: (_, variables) => {
      setIsDirty(false);
      message.success(t('Vaga atualizada'));

      if (
        !user.data?.is_staff &&
        ![variables.recruiter, variables.manager, ...(variables.related || [])].includes(
          user.data?.id
        )
      ) {
        navigate({ pathname: '/:tenant/opportunities/', params: { tenant } });
      } else if (searchParams.get('new')) {
        navigate({
          pathname: '/:tenant/opportunities/:id/change/publication/',
          params: { tenant, id, new: 1 },
        });
      } else if (leavePage) {
        navigate({ pathname: '/:tenant/opportunities/:id/', params: { tenant, id } });
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries('/job-postings/');
      await queryClient.invalidateQueries('/users/');
      await queryClient.invalidateQueries('/recruiters/');
    },
  });

  const recruiterUserId = recruiters.find(({ type }) => type === 'RECRUITER')?.user.id;
  const managerUserId = recruiters.find(({ type }) => type === 'MANAGER')?.user.id;
  const relatedUserIds = recruiters
    .filter(({ type }) => type === 'RELATED')
    .map(({ user }) => user.id);
  const observerUserIds = recruiters
    .filter(({ type }) => type === 'OBSERVER')
    .map(({ user }) => user.id);

  // Removing staff Users
  const recruiterInitialValue = users.find((user) => user.id === recruiterUserId)?.id;
  const managerInitialValue = users.find((user) => user.id === managerUserId)?.id;
  const relatedInitialValues = users
    .filter((user) => relatedUserIds.includes(user.id))
    .map((user) => user.id);
  const observersInitialValues = users
    .filter((user) => observerUserIds.includes(user.id))
    .map((user) => user.id);

  const recruiterUserOptions = users.filter(
    (user) => user.is_active || user.id === recruiterInitialValue
  );
  const managerUserOptions = users.filter(
    (user) => user.is_active || user.id === managerInitialValue
  );
  const relatedUserOptions = users.filter(
    (user) => user.is_active || relatedInitialValues.includes(user.id)
  );
  const observersUserOptions = users.filter(
    (user) => user.is_active || observersInitialValues.includes(user.id)
  );

  return (
    <ErrorResult error={jobPosting.error || usersPages.error || recruitersPages.error}>
      <PageContent>
        <Spin
          spinning={
            jobPosting.isFetching ||
            usersPages.isFetching ||
            recruitersPages.isFetching ||
            user.isFetching
          }
        >
          <ConfirmSelfRemoveModal
            key="ConfirmSelfRemove"
            open={confirmSelfRemoveModalIsOpen}
            okButtonProps={{ danger: true, type: 'default' }}
            onOk={() => {
              setConfirmSelfRemoveModalIsOpen(false);
              setSelfRemove(true);
              form.submit();
            }}
            onCancel={() => {
              setConfirmSelfRemoveModalIsOpen(false);
              setIsDirty(form.isFieldsTouched());
            }}
            onSuccess={() => {
              setConfirmSelfRemoveModalIsOpen(false);
            }}
            destroyOnClose
          />
          <Helmet>
            <title>
              {t('Configuração da vaga')} | {jobPosting.data?.name || '...'} |{' '}
              {t('Recrutamento e Seleção')}
            </title>
          </Helmet>
          <Form<AllowedUsersFormValues>
            form={form}
            layout="vertical"
            key={jobPosting.data?.id}
            validateMessages={{
              required: 'Campo obrigatório.',
            }}
            onFinish={(variables) => {
              if (
                !selfRemove &&
                !viewAllVacanciesPermission.data &&
                ![recruiterValue, managerValue, ...relatedValue].includes(user.data?.id)
              ) {
                setConfirmSelfRemoveModalIsOpen(true);
              } else {
                mutation.mutate({ ...variables, jobPostingId: id });
              }
            }}
            initialValues={{
              recruiter: recruiterInitialValue,
              manager: managerInitialValue,
              related: relatedInitialValues,
              observers: observersInitialValues,
            }}
            onValuesChange={(changedValues, values) => {
              if (changedValues.recruiter) {
                form.setFieldsValue({
                  ...values,
                  manager: changedValues.recruiter === values.manager ? undefined : values.manager,
                  related: values.related?.filter((userId) => userId !== changedValues.recruiter),
                  observers: values.observers?.filter(
                    (userId) => userId !== changedValues.recruiter
                  ),
                });
              }
              if (changedValues.manager) {
                form.setFieldsValue({
                  ...values,
                  related: values.related?.filter((userId) => userId !== changedValues.manager),
                  observers: values.observers?.filter((userId) => userId !== changedValues.manager),
                });
              }
              if (changedValues.related) {
                form.setFieldsValue({
                  observers: values.observers?.filter(
                    (userId) => !changedValues.related.includes(userId)
                  ),
                });
              }
              setIsDirty(form.isFieldsTouched());
            }}
          >
            <Form.Col span={8}>
              <Form.Item name="recruiter" label={t('Recrutador(a) responsável')}>
                <Select
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  data-testid="recruiter-select"
                >
                  {recruiterUserOptions.map((user) =>
                    user.full_name ? (
                      <Select.Option key={user.id} value={user.id}>
                        {user.full_name}
                      </Select.Option>
                    ) : (
                      <Select.Option key={user.id} value={user.id}>
                        {user.username}
                      </Select.Option>
                    )
                  )}
                </Select>
              </Form.Item>
              <Form.Item noStyle shouldUpdate={(prev, next) => prev.recruiter !== next.recruiter}>
                {(form) => (
                  <Form.Item name="manager" label={t('Gestor(a) da vaga')}>
                    <Select
                      allowClear
                      showSearch
                      filterOption={(input, option) =>
                        (option?.children as unknown as string)
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {managerUserOptions
                        .filter((user) => user.id !== form.getFieldValue('recruiter'))
                        .map((user) =>
                          user.full_name ? (
                            <Select.Option key={user.id} value={user.id}>
                              {user.full_name}
                            </Select.Option>
                          ) : (
                            <Select.Option key={user.id} value={user.id}>
                              {user.username}
                            </Select.Option>
                          )
                        )}
                    </Select>
                  </Form.Item>
                )}
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prev, next) =>
                  prev.recruiter !== next.recruiter || prev.manager !== next.manager
                }
              >
                {(form) => (
                  <Form.Item name="related" label={t('Envolvidos')}>
                    <Select
                      allowClear
                      mode="multiple"
                      showSearch
                      filterOption={(input, option) =>
                        (option?.children as unknown as string)
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {relatedUserOptions
                        .filter(
                          (user) =>
                            user.id !== form.getFieldValue('recruiter') &&
                            user.id !== form.getFieldValue('manager')
                        )
                        .map((user) =>
                          user.full_name ? (
                            <Select.Option key={user.id} value={user.id}>
                              {user.full_name}
                            </Select.Option>
                          ) : (
                            <Select.Option key={user.id} value={user.id}>
                              {user.username}
                            </Select.Option>
                          )
                        )}
                    </Select>
                  </Form.Item>
                )}
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prev, next) =>
                  prev.recruiter !== next.recruiter ||
                  prev.manager !== next.manager ||
                  prev.related.length !== next.related.length
                }
              >
                {(form) => (
                  <Form.Item
                    name="observers"
                    label={t('Observadores')}
                    tooltip={
                      <span>
                        <Trans>
                          Usuários marcados nesse campo poderão apenas visualizar a vaga. Eles
                        </Trans>{' '}
                        <Typography.Text style={{ color: 'inherit' }} strong>
                          <Trans>
                            não terão permissão para realizar ações, somente registrar comentários
                          </Trans>
                        </Typography.Text>
                      </span>
                    }
                  >
                    <Select
                      allowClear
                      mode="multiple"
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label || '').toLowerCase().includes(input.toLowerCase())
                      }
                      onSelect={(value) => {
                        if (value === user.data?.id) {
                          message.error(t('Você não pode se marcar como observador'));
                          form.setFieldValue(
                            'observers',
                            form
                              .getFieldValue('observers')
                              .filter((id: number) => id !== user.data?.id)
                          );
                        }
                      }}
                      options={observersUserOptions
                        .filter((user) => {
                          return (
                            user.id !== form.getFieldValue('recruiter') &&
                            user.id !== form.getFieldValue('manager') &&
                            !(form.getFieldValue('related') as number[]).find(
                              (id) => id === user.id
                            )
                          );
                        })
                        .map(({ id, full_name, username }) => ({
                          key: id,
                          label: full_name || username,
                          value: id,
                        }))}
                    />
                  </Form.Item>
                )}
              </Form.Item>
            </Form.Col>
            <Form.Footer>
              <Form.FooterEnd>
                <Button
                  onClick={() =>
                    navigate({ pathname: '/:tenant/opportunities/:id/', params: { tenant, id } })
                  }
                >
                  <Trans>Cancelar</Trans>
                </Button>
                <Button
                  loading={mutation.isLoading}
                  onClick={() => {
                    searchParams.delete('new');
                    setLeavePage(true);
                    form.submit();
                  }}
                >
                  <Trans>Salvar e Sair</Trans>
                </Button>
                <Button
                  type="primary"
                  loading={mutation.isLoading}
                  disabled={mutation.isLoading}
                  onClick={() => form.submit()}
                >
                  {searchParams.get('new') ? t('Salvar e continuar') : t('Salvar')}
                </Button>
              </Form.FooterEnd>
            </Form.Footer>
          </Form>
        </Spin>
      </PageContent>
    </ErrorResult>
  );
};

export default withRouteChange()(AllowedUsersForm);
